import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';

import type {
  MutableRefObject,
  ReactNode} from 'react';

import { Sticky } from '@travel/ui';
import { cx } from '@travel/utils';

import ButtonNoStyle from 'components/ButtonNoStyle';

import { setSelectedNavSection } from 'store/providerInformation/actions';
import { isClient } from 'store/utils';

import useDivElementHeight from 'hooks/useDivElementHeight';

import styles from './scrollingNavigation.module.scss';

export type Section = {
  id: string;
  title: string;
  target: MutableRefObject<HTMLDivElement | null>;
};

type Props = {
  sections: Section[];
  selectedSection?: string;
  otherChildren?: ReactNode;
  positionTop?: number;
};

// The distance from the element sticky to the top
const topDistance = 60;

function ScrollingNavigation(props: Props) {
  const { sections, selectedSection, otherChildren, positionTop = 0 } = props;
  const navSections = sections.filter(section => section);

  const [active, setActive] = useState<string>(selectedSection || navSections[0].id);
  const [itemWidth, setItemWidth] = useState<number | null>(null);
  const [itemLeft, setItemLeft] = useState<number | null>(null);
  const [contentTop, setContentTop] = useState<number | null>(null);

  const contentRef = useRef<HTMLDivElement | null>(null);
  const itemsRef = useRef<HTMLLIElement[] | null[]>([]);
  const contentHeight = useDivElementHeight(contentRef);

  const dispatch = useDispatch();

  const handleScroll = useCallback(() => {
    navSections.forEach(({ id, target }, index) => {
      const sectionRect = target.current?.getBoundingClientRect();

      const inViewThreshold = contentHeight + positionTop + topDistance; // Can be regarded as user enters this section when scrolling the screen to where the section is 60px under the navbar.

      setContentTop(Number(contentRef.current?.offsetTop));

      if (
        sectionRect &&
        sectionRect.top <= inViewThreshold &&
        sectionRect.bottom > inViewThreshold
      ) {
        const width = itemsRef.current[index]?.getBoundingClientRect().width;
        const left = itemsRef.current[index]?.offsetLeft;

        setActive(id);
        setItemWidth(Number(width));
        setItemLeft(Number(left));

        dispatch(setSelectedNavSection(navSections[index]));
      }
    });
  }, [contentHeight, dispatch, navSections, positionTop]);

  useEffect(() => {
    isClient && window.addEventListener('scroll', handleScroll);

    return () => {
      isClient && window.removeEventListener('scroll', handleScroll);
    };
  }, [handleScroll]);

  const handleNavItemClick = (id: string) => () => scrollToTarget(id);

  const scrollToTarget = useCallback(
    (sectionId: string) => {
      const offsetTop =
        navSections.find(({ id }) => id === sectionId)?.target.current?.offsetTop ?? 0;
      isClient &&
        window.scrollTo({
          top: offsetTop - contentHeight - positionTop,
          behavior: 'smooth',
        });
    },
    [contentHeight, navSections, positionTop],
  );

  return (
    <Sticky
      position="top"
      floatingStateClassName={styles.floating}
      className={styles.wrapper}
      rootMargin={`-${contentHeight + positionTop}px 0px 0px 0px`}
      childrenHeight={contentHeight}
      positionTop={positionTop}
    >
      <div
        className={styles.content}
        ref={contentRef}
        data-testid="scrollingNavigationBar-container"
      >
        <ul className={styles.navItemList}>
          {navSections.map(({ id, title }, index) => (
            <li
              className={cx(styles.navItem, id === active && styles.selectedItem)}
              key={id}
              data-testid={`scrollingNavigationBar-item-${id}`}
              data-selected={id === active}
              ref={el => (itemsRef.current[index] = el)}
            >
              <ButtonNoStyle onClick={handleNavItemClick(id)}>{title}</ButtonNoStyle>
              {index === 0 && contentTop === 0 ? (
                <hr
                  className={styles.activeBorder}
                  style={{
                    width: itemWidth || 0,
                    left: itemLeft || 0,
                  }}
                />
              ) : null}
            </li>
          ))}
        </ul>
        {otherChildren}
      </div>
    </Sticky>
  );
}

export default ScrollingNavigation;
